import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../apiClient';
import { Profile } from '../../hooks/useAuth';
import { format } from 'date-fns';
import {
  Wallet, Clock, CheckCircle2, ArrowUpRight, X, AlertCircle, Banknote,
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { SectionHeading } from '../ui/SectionHeading';
import { EmptyState } from '../ui/EmptyState';

interface Balance {
  pending_balance: number;
  available_balance: number;
  total_withdrawn: number;
}

interface LedgerEntry {
  id: string;
  booking_id: string;
  amount: number;
  status: 'pending' | 'settled';
  release_date: string;
  created_at: string;
}

interface ProviderProfile {
  bank_name: string | null;
  bank_account_number: string | null;
  bank_account_name: string | null;
}

export const EarningsManagement: React.FC<{ profile: Profile }> = () => {
  const [balance, setBalance] = useState<Balance | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [balData, ledgerData, profileData] = await Promise.all([
        apiFetch('/provider/balance'),
        apiFetch('/provider/balance-ledger'),
        apiFetch('/provider/profile'),
      ]);
      if (balData.success) setBalance(balData.balance);
      if (ledgerData.success) setLedger(ledgerData.ledger);
      if (profileData.success) {
        const p: ProviderProfile = profileData.profile;
        if (p.bank_name) setBankName(p.bank_name);
        if (p.bank_account_number) setAccountNumber(p.bank_account_number);
        if (p.bank_account_name) setAccountName(p.bank_account_name);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const data = await apiFetch('/provider/withdraw', {
        method: 'POST',
        body: JSON.stringify({
          amount: Number(withdrawAmount),
          bankDetails: { bank_name: bankName, account_number: accountNumber, account_name: accountName },
        }),
      });
      if (!data.success) throw new Error(data.message);
      setWithdrawSuccess(`Withdrawal request of RM ${Number(withdrawAmount).toFixed(2)} submitted.`);
      setIsModalOpen(false);
      setWithdrawAmount('');
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="p-12 flex items-center justify-center">
      <p className="label-caps text-ink-faint">Loading earnings...</p>
    </div>
  );

  const available = Number(balance?.available_balance ?? 0);

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-10 space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h2 className="display text-2xl md:text-3xl text-ink">Earnings</h2>
          <p className="label-caps text-ink-faint mt-2">Balance, ledger &amp; withdrawals</p>
        </div>
        <Button variant="primary" onClick={() => setIsModalOpen(true)} disabled={available <= 0}>
          <ArrowUpRight size={16} /> Request withdrawal
        </Button>
      </div>

      {error && (
        <div className="p-4 flex items-center gap-3 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded-sm border border-red-200 dark:border-red-900 text-xs font-semibold">
          <AlertCircle size={16} className="shrink-0" /> {error}
        </div>
      )}
      {withdrawSuccess && (
        <div className="p-4 flex items-center gap-3 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 rounded-sm border border-green-200 dark:border-green-900 text-xs font-semibold">
          <CheckCircle2 size={16} className="shrink-0" /> {withdrawSuccess}
        </div>
      )}

      {/* Balance cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <div className="bg-inverse text-ink-inverse p-8 rounded-sm border border-line">
          <p className="label-caps text-slate-400 mb-4">Pending release</p>
          <h3 className="display text-4xl leading-none mb-6">
            RM {Number(balance?.pending_balance ?? 0).toFixed(2)}
          </h3>
          <p className="label-caps text-accent flex items-center gap-2">
            <Clock size={14} /> Held 3-day escrow
          </p>
        </div>

        <Card accent className="p-8">
          <p className="label-caps text-ink-faint mb-4">Available to withdraw</p>
          <h3 className="display text-4xl text-ink leading-none mb-6">RM {available.toFixed(2)}</h3>
          <p className="label-caps text-green-600 dark:text-green-400 flex items-center gap-2">
            <Wallet size={14} /> Ready for payout
          </p>
        </Card>

        <Card className="p-8">
          <p className="label-caps text-ink-faint mb-4">Total withdrawn</p>
          <h3 className="display text-4xl text-ink leading-none mb-6">
            RM {Number(balance?.total_withdrawn ?? 0).toFixed(2)}
          </h3>
          <p className="label-caps text-ink-faint flex items-center gap-2">
            <CheckCircle2 size={14} /> Lifetime payouts
          </p>
        </Card>
      </div>

      {/* Ledger */}
      <Card className="overflow-hidden">
        <div className="px-6 md:px-8 py-5 border-b border-line">
          <SectionHeading>Earnings ledger</SectionHeading>
        </div>

        {ledger.length === 0 ? (
          <EmptyState icon={Banknote} title="No earnings yet" hint="Earnings appear after payments are confirmed" className="border-0" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[560px]">
              <thead className="bg-inverse">
                <tr>
                  <th className="px-6 md:px-8 py-4 label-caps text-slate-400">Booking</th>
                  <th className="px-6 md:px-8 py-4 label-caps text-slate-400">Amount</th>
                  <th className="px-6 md:px-8 py-4 label-caps text-slate-400">Release date</th>
                  <th className="px-6 md:px-8 py-4 label-caps text-slate-400 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {ledger.map(entry => (
                  <tr key={entry.id} className="hover:bg-surface transition-colors">
                    <td className="px-6 md:px-8 py-5 font-mono text-xs text-ink-faint">
                      {entry.booking_id?.slice(0, 8).toUpperCase()}…
                    </td>
                    <td className="px-6 md:px-8 py-5 display text-lg text-ink">
                      RM {Number(entry.amount).toFixed(2)}
                    </td>
                    <td className="px-6 md:px-8 py-5 text-xs font-semibold text-ink-muted">
                      {format(new Date(entry.release_date), 'dd MMM yyyy')}
                    </td>
                    <td className="px-6 md:px-8 py-5 text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-sm label-caps ${
                        entry.status === 'settled'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-line text-ink-muted'
                      }`}>
                        {entry.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Withdrawal modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-raised w-full max-w-md rounded-sm border border-line overflow-hidden">
            <div className="bg-inverse px-6 md:px-8 py-5 flex items-center justify-between">
              <h3 className="display text-lg text-ink-inverse">Request withdrawal</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white" aria-label="Close">
                <X size={22} />
              </button>
            </div>
            <form onSubmit={handleWithdraw} className="p-6 md:p-8 space-y-5">
              <div className="bg-surface border border-line rounded-sm p-4 text-center">
                <p className="label-caps text-ink-faint">Available</p>
                <p className="display text-3xl text-ink mt-1">RM {available.toFixed(2)}</p>
              </div>

              <Input
                label="Amount (RM)"
                type="number" required min="1" max={available} step="0.01"
                value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)}
                placeholder="0.00"
              />

              <div className="space-y-4">
                <p className="label-caps text-ink-muted">Bank details</p>
                <Input type="text" required value={bankName} onChange={e => setBankName(e.target.value)}
                  placeholder="Bank name (e.g. Maybank)" />
                <Input type="text" required value={accountNumber} onChange={e => setAccountNumber(e.target.value)}
                  placeholder="Account number" />
                <Input type="text" required value={accountName} onChange={e => setAccountName(e.target.value)}
                  placeholder="Account holder name" />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="ghost" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" className="flex-1" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit request'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
